import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy, Input, ElementRef } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { MenuItem, MessageService, ConfirmationService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { TabsModule } from "primeng/tabs";
import { ToastModule } from "primeng/toast";
import { ButtonModule } from "primeng/button";
import { ConfirmPopupModule } from "primeng/confirmpopup";
import { SessionService } from "../../../../core/services/session.service";
import {
  QPilotService,
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import {
  CustomerPortalSiteSettings,
  CustomerPortalSettingsForm,
  DetailedSummaryComponent,
  DEFAULT_DETAILED_SUMMARY_COMPONENTS,
  QPilotFrequencyTypeValues,
} from "../../interfaces/customer-portal-settings.interface";
import {
  CustomComponent,
  UnifiedComponentItem,
} from "../../interfaces/custom-component.interface";
import { CustomComponentStorageService } from "../../services/custom-component-storage.service";
import { GeneralSettingsComponent } from "./general-settings/general-settings.component";
import { ContentManagementComponent } from "./content-management/content-management.component";
import { StylingSettingsComponent } from "./styling-settings/styling-settings.component";
import { AdvancedConfigurationComponent } from "./advanced-configuration/advanced-configuration.component";
import { CustomerPortalSkeletonComponent } from "./customer-portal-skeleton.component";

@Component({
  selector: "app-customer-portal-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    TabsModule,
    ToastModule,
    ButtonModule,
    ConfirmPopupModule,
    GeneralSettingsComponent,
    ContentManagementComponent,
    StylingSettingsComponent,
    AdvancedConfigurationComponent,
    CustomerPortalSkeletonComponent,
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <div class="customer-portal-settings">
      <p-toast />
      <p-confirmPopup />
      <!-- Show skeleton only while fetching initial data -->
      <app-customer-portal-skeleton *ngIf="isLoading && !siteSettings" />

      @if (!isLoading || siteSettings) {
        <!-- Show actual content when loaded -->
        <form [formGroup]="formGroup" *ngIf="formGroup && siteSettings">
          <p-tabs
            scrollable
            [value]="activeTabIndex"
            (valueChange)="activeTabIndex = +$event"
          >
            <p-tablist>
              <!-- General Settings Tab -->
              <p-tab [value]="0">
                <i class="pi pi-cog mr-2"></i>
                General Settings
              </p-tab>

              <!-- Content Management Tab -->
              <p-tab [value]="1">
                <i class="pi pi-file-edit mr-2"></i>
                Content Management
              </p-tab>

              <!-- Styling Settings Tab -->
              <p-tab [value]="2">
                <i class="pi pi-palette mr-2"></i>
                Styling Settings
              </p-tab>

              <!-- Advanced Configuration Tab -->
              <p-tab [value]="3">
                <i class="pi pi-sliders-h mr-2"></i>
                Advanced Configuration
              </p-tab>
            </p-tablist>

            <p-tabpanels>
              <!-- General Settings Panel -->
              <p-tabpanel [value]="0">
                <app-general-settings
                  [formGroup]="formGroup"
                  [isLoading]="isLoading && !siteSettings"
                />
              </p-tabpanel>

              <!-- Content Management Panel -->
              <p-tabpanel [value]="1">
                <app-content-management
                  [formGroup]="formGroup"
                  [isLoading]="isLoading && !siteSettings"
                  [maxLength]="maxLength"
                  (goToLocalizationOutput)="goToLocalizationSettings()"
                  (loadDefaultOutput)="
                    loadDefaultSettings($event.field, $event.value)
                  "
                />
              </p-tabpanel>

              <!-- Styling Settings Panel -->
              <p-tabpanel [value]="2">
                <app-styling-settings
                  [formGroup]="formGroup"
                  [isLoading]="isLoading && !siteSettings"
                />
              </p-tabpanel>

              <!-- Advanced Configuration Panel -->
              <p-tabpanel [value]="3">
                <app-advanced-configuration
                  [formGroup]="formGroup"
                  [isLoading]="isLoading && !siteSettings"
                  [detailedSummaryComponents]="detailedSummaryComponents"
                  [siteId]="siteId"
                  [detailedSummaryConfig]="
                    siteSettings?.DetailedSummaryComponentConfig
                  "
                  (componentConfigChange)="updateComponentConfig()"
                  (customComponentsChange)="onCustomComponentsChange($event)"
                  (unifiedOrderChange)="onUnifiedOrderChange($event)"
                />
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>

          <!-- Sticky Bottom Actions - Button on left to avoid beacon chat -->
          <div class="actions-container">
            <div class="actions-card">
              <div class="actions-buttons">
                <p-button
                  #restoreButton
                  label="Restore to Defaults"
                  severity="secondary"
                  [outlined]="true"
                  (onClick)="confirmRestore($event)"
                  [disabled]="isUpdating || isRestoringDefaults"
                  [loading]="isRestoringDefaults"
                  size="small"
                />
                <p-button
                  label="Update"
                  [disabled]="disableForm || isUpdating || isRestoringDefaults"
                  [loading]="isUpdating"
                  (onClick)="update()"
                  size="small"
                />
              </div>
              <div class="changes-indicator" *ngIf="!disableForm">
                <i class="pi pi-info-circle"></i>
                <span>Changes will be applied immediately</span>
              </div>
            </div>
          </div>
        </form>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        background-color: white;
        overflow: hidden;
        min-height: 0;
      }

      .customer-portal-settings {
        display: flex;
        flex-direction: column;
        height: 100%; // Changed from calc to 100% to fill all available space
        width: 100%;
        min-height: 0;
        overflow: hidden;
      }

      .content-section {
        padding: 0 1rem;
        flex-shrink: 0;
      }

      form {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      // Scoped to .customer-portal-settings to avoid leaking to other components
      .customer-portal-settings ::ng-deep p-tabView {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        padding: 0;
      }

      .customer-portal-settings ::ng-deep p-tabView .p-tabview {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
      }

      .customer-portal-settings ::ng-deep p-tabView .p-tabview-panels {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0;
        min-height: 0;
        position: relative;
      }

      // Tab Styling - Full height
      // Scoped to .customer-portal-settings to avoid leaking to other components (like command palette)
      .customer-portal-settings ::ng-deep p-tabs {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      .customer-portal-settings ::ng-deep p-tabs p-tablist {
        flex-shrink: 0;
        margin-bottom: 0;
      }

      .customer-portal-settings ::ng-deep p-tabs p-tabpanels {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
      }

      .customer-portal-settings ::ng-deep p-tabs p-tabpanel {
        padding: 1rem;
        overflow: visible;
        box-sizing: border-box;
        min-height: min-content;
      }

      .actions-container {
        margin-top: auto;
        padding: 0.5rem 0 0 0;
        flex-shrink: 0;
      }

      .actions-card {
        background: white;
        border-radius: 12px;
        padding: 1rem;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .changes-indicator {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #64748b; // Changed from #3b82f6 (blue) to gray
        font-size: 0.8125rem; // Smaller
        font-weight: 400; // Changed from 500
      }

      .changes-indicator i {
        font-size: 0.8125rem;
      }

      .actions-buttons {
        display: flex;
        gap: 0.75rem;
        align-items: center;
        // Removed margin-left: auto so buttons stay on left
      }
    `,
  ],
})
export class CustomerPortalSettingsComponent implements OnInit, OnDestroy {
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;

  siteId!: number;
  siteSettings!: CustomerPortalSiteSettings;
  isLoading: boolean = false;
  isUpdating: boolean = false;
  isRestoringDefaults: boolean = false;

  formGroup!: FormGroup<CustomerPortalSettingsForm>;
  disableForm: boolean = true;
  maxLength = 512;
  detailedSummaryComponents: DetailedSummaryComponent[] = [];
  activeTabIndex: number = 0;

  // Custom components for API persistence
  customComponents: CustomComponent[] = [];
  unifiedOrder: UnifiedComponentItem[] = [];

  breadcrumbItems: MenuItem[] = [];
  home: MenuItem = { icon: "pi pi-home", routerLink: "/" };

  // Subject for cleanup on destroy
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly qpilot: QPilotService,
    private readonly sessionService: SessionService,
    private readonly formBuilder: FormBuilder,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly elementRef: ElementRef,
    private readonly customComponentStorage: CustomComponentStorageService,
  ) {}

  ngOnInit(): void {
    // Get siteId and token from attributes (kebab-case) OR inputs (camelCase)
    const element = this.elementRef.nativeElement as HTMLElement;
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId") ??
      "0";
    const siteId = parseInt(siteIdString, 10);
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    // Set the values from attributes if they exist (like subscriber-portal does)
    if (siteId) {
      this.siteId = siteId;
    }
    if (tokenString) {
      // Convert token string to QPilotAccessToken object for embedded usage
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // this.setupBreadcrumbs();
    this.initializeFormGroup();
    this.setupRouteSubscription();
  }

  private setupBreadcrumbs(): void {
    this.route.parent?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const siteId = params.get("siteId");

        if (!siteId) {
          console.error(
            "No siteId found in parent route parameters for breadcrumbs",
          );
          return;
        }

        this.breadcrumbItems = [
          { label: "Sites", routerLink: "/sites" },
          { label: "Dashboard", routerLink: `/widgets/dashboard/${siteId}` },
          {
            label: "Settings",
            routerLink: `/widgets/dashboard/${siteId}/settings`,
          },
          {
            label: "Customer Portal",
            routerLink: `/widgets/dashboard/${siteId}/settings/customer-portal`,
          },
        ];

        this.home = { icon: "pi pi-home", routerLink: "/sites" };
      });
  }

  private setupRouteSubscription(): void {
    // Use inputSiteId if provided, otherwise get from route
    if (this.inputSiteId) {
      this.siteId = this.inputSiteId;
      this.fetchSite();
    } else {
      this.route.parent?.params
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          this.siteId = +params["siteId"];
          this.fetchSite();
        });
    }
  }

  private initializeFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      // General Settings
      isShowingNotes: [false],
      isShowCoupons: [false],
      allowRetryScheduledOrder: [false],

      // Frequency Settings
      manageAllowedFrequencies: [false],
      allowedFrequencies: this.formBuilder.nonNullable.array<
        FormControl<QPilotFrequencyTypeValues>
      >([]),

      // Product Settings
      sortProductsAlphabetically: [false],
      hideOutOfStockProducts: [false],

      // Styling Settings
      isStackedButtons: [false],
      isDisplayScheduledOrderId: [false],
      enableProductCarouselAutoSlide: [false],
      showProductTitleOnImages: [false],

      // No Scheduled Order Settings
      noScheduleOrdersBody: ["", [Validators.maxLength(500)]],
      noScheduleOrdersLink: ["", [Validators.maxLength(200)]],

      // Color Settings
      primaryColor: [
        "#000000",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      onPrimaryColor: [
        "#FFFFFF",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      surfaceColor: [
        "#FFFFFF",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      onSurfaceColor: [
        "#000000",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      errorColor: [
        "#DC2626",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      onErrorColor: [
        "#FFFFFF",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      discountBarColor: [
        "#10B981",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      onDiscountBarColor: [
        "#FFFFFF",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],

      // Button Settings
      buttonBorderThicknessPx: [0, [Validators.min(0), Validators.max(10)]],
      buttonBorderRadiusPx: [0, [Validators.min(0), Validators.max(50)]],
      buttonBorderColor: [
        "#E5E7EB",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      selectedButtonBackgroundColor: [
        "#3B82F6",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      selectedButtonTextColor: [
        "#FFFFFF",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      unselectedButtonTextColor: [
        "#374151",
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],

      // Image Settings
      maxImageWidthPx: [300, [Validators.min(0), Validators.max(700)]],
      maxImageHeightPx: [300, [Validators.min(0), Validators.max(700)]],

      // Custom Styles
      customStyles: [""],
      detailedSummaryCustomCss: [""],
    });

    // Subscribe to form changes to enable/disable update button
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.disableForm = !this.isFormUpdated() || this.formGroup.invalid;
    });
  }

  private populateFormGroup(): void {
    if (!this.siteSettings) return;

    // Clear and populate the allowedFrequencies FormArray
    const allowedFrequenciesArray = this.formGroup.controls.allowedFrequencies;
    allowedFrequenciesArray.clear();
    if (this.siteSettings.AllowedFrequencies) {
      this.siteSettings.AllowedFrequencies.forEach((freq) => {
        allowedFrequenciesArray.push(
          this.formBuilder.nonNullable.control(freq),
        );
      });
    }

    this.formGroup.patchValue({
      // General Settings
      isShowingNotes: this.siteSettings.IsShowingNotes,
      isShowCoupons: this.siteSettings.IsShowCoupons,
      allowRetryScheduledOrder: this.siteSettings.AllowRetryScheduledOrder,

      // Frequency Settings
      manageAllowedFrequencies: this.siteSettings.ManageAllowedFrequencies,

      // Product Settings
      sortProductsAlphabetically: this.siteSettings.SortProductsAlphabetically,
      hideOutOfStockProducts: this.siteSettings.HideOutOfStockProducts,

      // Styling Settings
      isStackedButtons: this.siteSettings.IsStackedButtons,
      isDisplayScheduledOrderId: this.siteSettings.IsDisplayScheduledOrderId,
      enableProductCarouselAutoSlide:
        this.siteSettings.EnableProductCarouselAutoSlide,
      showProductTitleOnImages: this.siteSettings.ShowProductTitleOnImages,

      // No Scheduled Order Settings
      noScheduleOrdersBody: this.siteSettings.NoScheduleOrdersBody,
      noScheduleOrdersLink: this.siteSettings.NoScheduleOrdersLink,

      // Color Settings
      primaryColor: this.siteSettings.Styles.PrimaryColor,
      onPrimaryColor: this.siteSettings.Styles.OnPrimaryColor,
      surfaceColor: this.siteSettings.Styles.SurfaceColor,
      onSurfaceColor: this.siteSettings.Styles.OnSurfaceColor,
      errorColor: this.siteSettings.Styles.ErrorColor,
      onErrorColor: this.siteSettings.Styles.OnErrorColor,
      discountBarColor: this.siteSettings.Styles.DiscountBarColor,
      onDiscountBarColor: this.siteSettings.Styles.OnDiscountBarColor,

      // Button Settings
      buttonBorderThicknessPx: this.siteSettings.Styles.ButtonBorderThicknessPx,
      buttonBorderRadiusPx: this.siteSettings.Styles.ButtonBorderRadiusPx,
      buttonBorderColor: this.siteSettings.Styles.ButtonBorderColor,
      selectedButtonBackgroundColor:
        this.siteSettings.Styles.SelectedButtonBackgroundColor,
      selectedButtonTextColor: this.siteSettings.Styles.SelectedButtonTextColor,
      unselectedButtonTextColor:
        this.siteSettings.Styles.UnselectedButtonTextColor,

      // Image Settings
      maxImageWidthPx: this.siteSettings.Styles.MaxImageWidthPx,
      maxImageHeightPx: this.siteSettings.Styles.MaxImageHeightPx,

      // Custom Styles
      customStyles: this.siteSettings.Styles.CustomStyles,
      detailedSummaryCustomCss:
        this.siteSettings.DetailedSummaryComponentConfig.CustomCss,
    });
  }

  fetchSite(): void {
    // Use inputToken if provided, otherwise use session token
    const token = this.inputToken || this.sessionService.accessToken;

    if (!token) {
      console.error("No access token available");
      return;
    }

    this.isLoading = true;

    this.qpilot.getCustomerPortalSiteSettings(this.siteId, token).subscribe({
      next: (siteSettings: CustomerPortalSiteSettings) => {
        this.siteSettings = siteSettings;
        this.populateFormGroup();
        this.initializeDetailedSummaryComponents();
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load customer portal settings",
        });
      },
    });
  }

  public isFormUpdated(): boolean {
    if (!this.siteSettings || !this.formGroup) {
      return false;
    }

    const formValues = this.formGroup.value;

    // Check if the detailed summary component configuration has changed
    const detailedSummaryCustomCssChanged =
      this.siteSettings.DetailedSummaryComponentConfig &&
      this.siteSettings.DetailedSummaryComponentConfig.CustomCss !==
        formValues.detailedSummaryCustomCss;

    // Check if any component has been enabled/disabled or reordered
    const componentsChanged = this.detailedSummaryComponents.some((comp) => {
      let existingComp = null;
      if (
        this.siteSettings.DetailedSummaryComponentConfig &&
        this.siteSettings.DetailedSummaryComponentConfig.Components
      ) {
        existingComp =
          this.siteSettings.DetailedSummaryComponentConfig.Components.find(
            (c) => c.Identifier === comp.Identifier,
          );
      }
      return (
        (!existingComp && comp.Enabled) ||
        (existingComp && !comp.Enabled) ||
        (existingComp && existingComp.Order !== comp.Order)
      );
    });

    // Check if frequencies have changed
    const frequenciesChanged =
      this.siteSettings.ManageAllowedFrequencies !==
        formValues.manageAllowedFrequencies ||
      JSON.stringify(this.siteSettings.AllowedFrequencies || []) !==
        JSON.stringify(this.formGroup.controls.allowedFrequencies.value || []);

    return (
      this.siteSettings.IsShowingNotes !== formValues.isShowingNotes ||
      this.siteSettings.IsShowCoupons !== formValues.isShowCoupons ||
      this.siteSettings.AllowRetryScheduledOrder !==
        formValues.allowRetryScheduledOrder ||
      frequenciesChanged ||
      this.siteSettings.SortProductsAlphabetically !==
        formValues.sortProductsAlphabetically ||
      this.siteSettings.HideOutOfStockProducts !==
        formValues.hideOutOfStockProducts ||
      this.siteSettings.IsStackedButtons !== formValues.isStackedButtons ||
      this.siteSettings.IsDisplayScheduledOrderId !==
        formValues.isDisplayScheduledOrderId ||
      this.siteSettings.EnableProductCarouselAutoSlide !==
        formValues.enableProductCarouselAutoSlide ||
      this.siteSettings.ShowProductTitleOnImages !==
        formValues.showProductTitleOnImages ||
      this.siteSettings.NoScheduleOrdersBody !==
        formValues.noScheduleOrdersBody ||
      this.siteSettings.NoScheduleOrdersLink !==
        formValues.noScheduleOrdersLink ||
      this.siteSettings.Styles.PrimaryColor !== formValues.primaryColor ||
      this.siteSettings.Styles.OnPrimaryColor !== formValues.onPrimaryColor ||
      this.siteSettings.Styles.SurfaceColor !== formValues.surfaceColor ||
      this.siteSettings.Styles.OnSurfaceColor !== formValues.onSurfaceColor ||
      this.siteSettings.Styles.ErrorColor !== formValues.errorColor ||
      this.siteSettings.Styles.OnErrorColor !== formValues.onErrorColor ||
      this.siteSettings.Styles.DiscountBarColor !==
        formValues.discountBarColor ||
      this.siteSettings.Styles.OnDiscountBarColor !==
        formValues.onDiscountBarColor ||
      this.siteSettings.Styles.ButtonBorderThicknessPx !==
        formValues.buttonBorderThicknessPx ||
      this.siteSettings.Styles.ButtonBorderRadiusPx !==
        formValues.buttonBorderRadiusPx ||
      this.siteSettings.Styles.ButtonBorderColor !==
        formValues.buttonBorderColor ||
      this.siteSettings.Styles.SelectedButtonBackgroundColor !==
        formValues.selectedButtonBackgroundColor ||
      this.siteSettings.Styles.SelectedButtonTextColor !==
        formValues.selectedButtonTextColor ||
      this.siteSettings.Styles.UnselectedButtonTextColor !==
        formValues.unselectedButtonTextColor ||
      this.siteSettings.Styles.MaxImageWidthPx !== formValues.maxImageWidthPx ||
      this.siteSettings.Styles.MaxImageHeightPx !==
        formValues.maxImageHeightPx ||
      this.siteSettings.Styles.CustomStyles !== formValues.customStyles ||
      detailedSummaryCustomCssChanged ||
      componentsChanged
    );
  }

  private mapFormFields(): void {
    if (!this.formGroup || !this.siteSettings) return;

    const formValues = this.formGroup.value;

    // General Settings
    this.siteSettings.IsShowingNotes = formValues.isShowingNotes ?? false;
    this.siteSettings.IsShowCoupons = formValues.isShowCoupons ?? false;
    this.siteSettings.AllowRetryScheduledOrder =
      formValues.allowRetryScheduledOrder ?? false;

    // Frequency Settings
    this.siteSettings.ManageAllowedFrequencies =
      formValues.manageAllowedFrequencies ?? false;
    this.siteSettings.AllowedFrequencies =
      this.formGroup.controls.allowedFrequencies.value ?? [];

    // Product Settings
    this.siteSettings.SortProductsAlphabetically =
      formValues.sortProductsAlphabetically ?? false;
    this.siteSettings.HideOutOfStockProducts =
      formValues.hideOutOfStockProducts ?? false;

    // Styling Settings
    this.siteSettings.IsStackedButtons = formValues.isStackedButtons ?? false;
    this.siteSettings.IsDisplayScheduledOrderId =
      formValues.isDisplayScheduledOrderId ?? false;
    this.siteSettings.EnableProductCarouselAutoSlide =
      formValues.enableProductCarouselAutoSlide ?? false;
    this.siteSettings.ShowProductTitleOnImages =
      formValues.showProductTitleOnImages ?? false;

    // Content Settings
    this.siteSettings.NoScheduleOrdersBody =
      formValues.noScheduleOrdersBody ?? "";
    this.siteSettings.NoScheduleOrdersLink =
      formValues.noScheduleOrdersLink ?? "";

    // Update styles
    this.siteSettings.Styles.PrimaryColor = formValues.primaryColor ?? "";
    this.siteSettings.Styles.OnPrimaryColor = formValues.onPrimaryColor ?? "";
    this.siteSettings.Styles.SurfaceColor = formValues.surfaceColor ?? "";
    this.siteSettings.Styles.OnSurfaceColor = formValues.onSurfaceColor ?? "";
    this.siteSettings.Styles.ErrorColor = formValues.errorColor ?? "";
    this.siteSettings.Styles.OnErrorColor = formValues.onErrorColor ?? "";
    this.siteSettings.Styles.DiscountBarColor =
      formValues.discountBarColor ?? "";
    this.siteSettings.Styles.OnDiscountBarColor =
      formValues.onDiscountBarColor ?? "";
    this.siteSettings.Styles.ButtonBorderThicknessPx =
      formValues.buttonBorderThicknessPx ?? 0;
    this.siteSettings.Styles.ButtonBorderRadiusPx =
      formValues.buttonBorderRadiusPx ?? 0;
    this.siteSettings.Styles.ButtonBorderColor =
      formValues.buttonBorderColor ?? "";
    this.siteSettings.Styles.SelectedButtonBackgroundColor =
      formValues.selectedButtonBackgroundColor ?? "";
    this.siteSettings.Styles.SelectedButtonTextColor =
      formValues.selectedButtonTextColor ?? "";
    this.siteSettings.Styles.UnselectedButtonTextColor =
      formValues.unselectedButtonTextColor ?? "";
    this.siteSettings.Styles.MaxImageWidthPx = formValues.maxImageWidthPx ?? 0;
    this.siteSettings.Styles.MaxImageHeightPx =
      formValues.maxImageHeightPx ?? 0;
    this.siteSettings.Styles.CustomStyles = formValues.customStyles ?? "";

    // Update the detailed summary component configuration
    this.updateComponentConfig();
  }

  goToLocalization(): void {
    // Navigate to localization settings - implement based on your routing structure
    void this.router.navigate(["../localization"], { relativeTo: this.route });
  }

  loadDefault(field: string, value: string | null): void {
    this.formGroup.get(field)?.setValue(value);
  }

  setColor(color: string, field: string): void {
    this.formGroup.get(field)?.setValue(color);
  }

  update(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Please fix validation errors before saving",
      });
      return;
    }
    const token = this.inputToken || this.sessionService.accessToken;

    if (!this.formGroup || !token) {
      return;
    }

    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      this.messageService.add({
        severity: "error",
        summary: "Invalid Form",
        detail: "Please correct the form errors before submitting",
      });
      return;
    }

    this.mapFormFields();
    this.isUpdating = true;

    this.qpilot
      .editCustomerPortalSiteSettings(
        this.siteId,
        this.siteSettings,
        this.inputToken || this.sessionService.accessToken!,
      )
      .subscribe({
        next: (siteSettings: CustomerPortalSiteSettings) => {
          this.siteSettings = siteSettings;
          this.populateFormGroup();
          this.initializeDetailedSummaryComponents();
          this.disableForm = true;
          this.isUpdating = false;
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Your site has been updated.",
          });
        },
        error: (error) => {
          console.error(error);
          this.isUpdating = false;
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to update customer portal settings",
          });
        },
      });
  }

  confirmRestore(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: "Are you sure you want to restore to default values?",
      header: "Confirm Restore",
      icon: "pi pi-exclamation-triangle",
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.restoreDefaults();
      },
    });
  }

  private restoreDefaults(): void {
    if (!this.siteSettings) return;

    // Reset General Settings to defaults
    this.siteSettings.IsShowingNotes = false;
    this.siteSettings.IsShowCoupons = false;
    this.siteSettings.AllowRetryScheduledOrder = false;

    // Reset Frequency Settings to defaults
    this.siteSettings.ManageAllowedFrequencies = false;
    this.siteSettings.AllowedFrequencies = [];

    // Reset Product Settings to defaults
    this.siteSettings.SortProductsAlphabetically = false;
    this.siteSettings.HideOutOfStockProducts = false;

    // Reset Layout Settings to defaults
    this.siteSettings.IsStackedButtons = false;
    this.siteSettings.IsDisplayScheduledOrderId = false;
    this.siteSettings.EnableProductCarouselAutoSlide = false;
    this.siteSettings.ShowProductTitleOnImages = false;

    // Reset Content Settings to defaults
    this.siteSettings.NoScheduleOrdersBody = "";
    this.siteSettings.NoScheduleOrdersLink = "";

    // Reset the DetailedSummaryComponentConfig to default but preserve CustomCss
    const existingCustomCss =
      (this.siteSettings.DetailedSummaryComponentConfig &&
        this.siteSettings.DetailedSummaryComponentConfig.CustomCss) ||
      "";

    this.siteSettings.DetailedSummaryComponentConfig = {
      CustomCss: existingCustomCss,
      Components: [],
    };

    // Restore all style properties except customStyles
    const currentCustomStyles = this.siteSettings.Styles.CustomStyles;
    this.siteSettings.Styles = { ...this.siteSettings.DefaultStyles };
    this.siteSettings.Styles.CustomStyles = currentCustomStyles;

    this.populateFormGroup();
    this.initializeDetailedSummaryComponents();

    const token = this.inputToken || this.sessionService.accessToken;

    if (!token) {
      console.error("No access token available");
      return;
    }

    this.isRestoringDefaults = true;

    // Call update but track it as restore operation
    this.mapFormFields();

    this.qpilot
      .editCustomerPortalSiteSettings(this.siteId, this.siteSettings, token)
      .subscribe({
        next: (siteSettings: CustomerPortalSiteSettings) => {
          this.siteSettings = siteSettings;
          this.populateFormGroup();
          this.initializeDetailedSummaryComponents();
          this.disableForm = true;
          this.isRestoringDefaults = false;
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Settings have been restored to defaults.",
          });
        },
        error: (error) => {
          console.error(error);
          this.isRestoringDefaults = false;
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to restore default settings",
          });
        },
      });
  }

  goToLocalizationSettings(): void {
    void this.router.navigate(["../localization"], { relativeTo: this.route });
  }

  loadDefaultSettings(field: string, value: string | null): void {
    this.formGroup.get(field)?.setValue(value);
  }

  initializeDetailedSummaryComponents(): void {
    if (!this.siteSettings) return;

    if (!this.siteSettings.DetailedSummaryComponentConfig) {
      this.siteSettings.DetailedSummaryComponentConfig = {
        CustomCss: "",
        Components: [],
      };
    }

    if (
      this.siteSettings.DetailedSummaryComponentConfig.Components &&
      this.siteSettings.DetailedSummaryComponentConfig.Components.length > 0
    ) {
      const existingComponents =
        this.siteSettings.DetailedSummaryComponentConfig.Components;
      const existingComponentMap = new Map<string, DetailedSummaryComponent>();
      existingComponents.forEach((comp) => {
        existingComponentMap.set(comp.Identifier, comp);
      });

      this.detailedSummaryComponents = DEFAULT_DETAILED_SUMMARY_COMPONENTS.map(
        (defaultComp, index) => {
          const existingComp = existingComponentMap.get(defaultComp.Identifier);
          return {
            Order: existingComp ? existingComp.Order : index,
            Label: defaultComp.Label,
            Identifier: defaultComp.Identifier,
            Enabled: existingComp ? existingComp.Enabled : false,
          };
        },
      );

      this.detailedSummaryComponents.sort((a, b) => a.Order - b.Order);
    } else {
      this.detailedSummaryComponents = DEFAULT_DETAILED_SUMMARY_COMPONENTS.map(
        (comp, index) => ({
          Order: index,
          Label: comp.Label,
          Identifier: comp.Identifier,
          Enabled: true,
        }),
      );
    }

    // Initialize custom components and unified order from API response
    this.initializeCustomComponentsFromConfig();
  }

  /**
   * Initialize custom components and unified order from API config
   */
  private initializeCustomComponentsFromConfig(): void {
    const config = this.siteSettings?.DetailedSummaryComponentConfig;

    // Extract custom components from API config
    this.customComponents =
      this.customComponentStorage.getCustomComponentsFromConfig(config);

    // Get existing unified order from API config
    const existingUnifiedOrder =
      this.customComponentStorage.getUnifiedOrderFromConfig(
        config,
        this.customComponents,
      );

    // Initialize/sync unified order
    this.unifiedOrder = this.customComponentStorage.initializeUnifiedOrder(
      this.detailedSummaryComponents,
      this.customComponents,
      existingUnifiedOrder,
    );
  }

  updateComponentConfig(): void {
    if (!this.siteSettings) return;

    const enabledComponents = this.detailedSummaryComponents.map((comp) => ({
      Order: comp.Order,
      Label: comp.Label,
      Identifier: comp.Identifier,
      Enabled: comp.Enabled,
    }));

    // Update the config with standard components, custom components, and unified order
    this.siteSettings.DetailedSummaryComponentConfig =
      this.customComponentStorage.updateConfigForSave(
        {
          CustomCss:
            this.formGroup.get("detailedSummaryCustomCss")?.value || "",
          Components: enabledComponents,
          UnifiedOrder:
            this.siteSettings.DetailedSummaryComponentConfig?.UnifiedOrder ||
            null,
          CustomComponents:
            this.siteSettings.DetailedSummaryComponentConfig
              ?.CustomComponents || null,
        },
        this.unifiedOrder,
        this.customComponents,
      );

    this.disableForm = false;
  }

  /**
   * Handle custom components change from advanced-configuration
   * Updates the local array and marks form as dirty
   */
  onCustomComponentsChange(components: CustomComponent[]): void {
    this.customComponents = components;
    this.disableForm = false;
  }

  /**
   * Handle unified order change from advanced-configuration
   * Updates the local array and marks form as dirty
   */
  onUnifiedOrderChange(order: UnifiedComponentItem[]): void {
    this.unifiedOrder = order;
    this.disableForm = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
